import { EventEmitter, Injectable } from '@angular/core';
import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import * as signalR from '@microsoft/signalr';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { NbAuthService } from '@nebular/auth';

export interface ProcessingTaskInfo {
  taskName: string;
  message?: string;
  ratio?: number;
  startedAt: string;
}

export interface ProcessingStatus {
  itemId: string;
  path: string;
  processingTasks: { [taskName: string]: ProcessingTaskInfo };
  processingProperties?: string[]; // Backward compatibility
  status: 'Processing' | 'Done';
  startedAt: string;
  lastUpdatedAt: string;
  // Optional extra payload coming from backend (arbitrary shape: object/array/primitive)
  payload?: any;
}

/**
 * @summary Client-side tracker for background processing status via SignalR.
 * @description
 * ### What this does
 * - Connects to `ProcessingStatusHub` and listens for status updates.
 * - Stores the latest `ProcessingHubsViewModel` per `itemId` for UI consumption as a list.
 * - Supports both **granular** (task/property) subscriptions and **whole-item** subscriptions.
 *
 * ### Usage (recommended)
 * - Subscribe once when entering a screen:
 *   - `subscribeToItem(itemId, path)` to receive all updates for the item.
 *   - `subscribeToProperties(itemId, path, ['SeoInfo'])` to receive updates for specific tasks only.
 * - Render using:
 *   - `getProcessingStatuses$()` (Observable) or `processingStatus$` (stream)
 *   - `isTaskProcessing(itemId, path, taskName)` / `getTaskInfo(itemId, path, taskName)`
 *
 * ### Example (legacy subscribe pattern)
 * ```
 * this.processingStatusSubscription = this.processingStatusService.getProcessingStatuses$()
 *   .subscribe(() => {
 *     this.isProcessingSeo = this.processingStatusService.isTaskProcessing(this.id, '/profile', 'SeoInfo');
 *     this.seoProcessingMessage = this.processingStatusService.getTaskInfo(this.id, '/profile', 'SeoInfo')?.message;
 *   });
 *
 * this.processingStatusService.subscribeToProperties(this.id, '/profile', ['SeoInfo'])
 *   .catch(console.error);
 * ```
 *
 * ### Example (async/await)
 * ```
 * await this.processingStatusService.subscribeToItem(this.id, '/profile');
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class ProcessingStatusService {
  private hubConnection: HubConnection | null = null;
  private connectionState$ = new BehaviorSubject<HubConnectionState>(HubConnectionState.Disconnected);
  private processingStatuses$ = new BehaviorSubject<Map<string, ProcessingStatus>>(new Map());
  private isInitializing = false;
  private initializationPromise: Promise<void> | null = null;
  private subscribedItems = new Set<string>();
  private subscribedProperties = new Map<string, string[]>();
  private subscribedItemPaths = new Map<string, string>(); // Track path for each item
  private subscribedPropertyPaths = new Map<string, string>(); // Track path for each property subscription
  private connectionIsEstablished = false;
  private handlersRegistered = false;
  connectionEstablished = new EventEmitter<boolean>();
  token: string;

  // Registry for DONE-only listeners by statusKey (itemId::path)
  // Emit full hub payload so different use-cases can reuse extra data (version, arrays, objects, etc.)
  private itemDoneEmitters = new Map<string, EventEmitter<any>>();

  constructor(private authService: NbAuthService) {
  }

  /**
   * Connect to ProcessingStatusHub (global initialization)
   * Similar to SignalrDynamicService.connect() pattern
   * @param token Optional token, if not provided will get from authService
   */
  connect(token?: string): void {
    // Prevent duplicate connection attempts
    if (this.connectionIsEstablished && this.hubConnection?.state === HubConnectionState.Connected) {
      this.connectionEstablished.emit(true);
      return;
    }

    if (token) {
      this.token = token;
    }
    this.initializeConnection().then(() => {
      this.connectionIsEstablished = true;
      this.connectionEstablished.emit(true);
    }).catch((error) => {
      console.error('Error connecting to ProcessingStatusHub:', error);
      this.connectionIsEstablished = false;
      this.connectionEstablished.emit(false);
    });
  }

  private async initializeConnection(): Promise<void> {
    // If already initializing, wait for that promise
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    // If already initialized and connected, return
    if (this.hubConnection && this.hubConnection.state === HubConnectionState.Connected) {
      return;
    }

    // If already initialized but disconnected, try to start
    // Since hub is initialized globally, handlers should already be registered
    if (this.hubConnection && this.hubConnection.state !== HubConnectionState.Connected) {
      try {
        // Handlers are already registered from initial global connection setup
        await this.hubConnection.start();
        this.connectionState$.next(this.hubConnection.state);
        await this.reSubscribeAll();
        return;
      } catch (error) {
        console.error('Error restarting ProcessingStatusHub connection:', error);
        // Fall through to recreate connection
        this.hubConnection = null;
        this.handlersRegistered = false;
      }
    }

    this.isInitializing = true;
    this.initializationPromise = this.doInitializeConnection();

    try {
      await this.initializationPromise;
    } finally {
      this.isInitializing = false;
      this.initializationPromise = null;
    }
  }

  private async doInitializeConnection(): Promise<void> {
    const hubUrl = `${environment.host}/dynamicHub/processing`;

    try {
      // Use provided token or get from authService
      let token = this.token;
      if (!token) {
        const authToken = await this.authService.getToken().toPromise();
        token = authToken?.getValue() || '';
      }

      this.hubConnection = new HubConnectionBuilder()
        .withUrl(hubUrl, {
          accessTokenFactory: async () => {
            try {
              // Use stored token if available, otherwise get from authService
              if (this.token) {
                return this.token;
              }
              const authToken = await this.authService.getToken().toPromise();
              return authToken?.getValue() || '';
            } catch (error) {
              console.error('Error getting auth token:', error);
              return '';
            }
          }
        })
        .configureLogging(signalR.LogLevel.Warning)
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext) => {
            if (retryContext.previousRetryCount < 3) {
              return 1000; // 1 second
            } else if (retryContext.previousRetryCount < 10) {
              return 5000; // 5 seconds
            } else {
              return 30000; // 30 seconds
            }
          }
        })
        .build();

      // Register event handlers BEFORE starting connection
      // Since hub is initialized globally, this should only be called once
      this.registerEventHandlers();
      this.handlersRegistered = true;

      // Handle connection state changes
      this.hubConnection.onclose((error) => {
        this.connectionState$.next(HubConnectionState.Disconnected);
        if (error) {
          console.error('ProcessingStatusHub connection closed with error:', error);
        }
      });

      this.hubConnection.onreconnecting((error) => {
        this.connectionState$.next(HubConnectionState.Reconnecting);
        if (error) {
          console.warn('ProcessingStatusHub reconnecting due to error:', error);
        }
      });

      this.hubConnection.onreconnected((connectionId) => {
        this.connectionState$.next(HubConnectionState.Connected);
        // Re-subscribe to all items when reconnected
        this.reSubscribeAll().catch(err => {
          console.error('Error re-subscribing after reconnect:', err);
        });
      });

      // Start connection
      await this.hubConnection.start();
      this.connectionState$.next(this.hubConnection.state);
      this.connectionIsEstablished = true;
      this.connectionEstablished.emit(true);
    } catch (error) {
      console.error('Error initializing ProcessingStatusHub connection:', error);
      this.connectionState$.next(HubConnectionState.Disconnected);
      this.connectionIsEstablished = false;
      throw error;
    }
  }

  /**
   * Dispose connection (cleanup)
   */
  onDispose(): void {
    if (this.hubConnection) {
      this.hubConnection.stop();
      this.hubConnection = null;
    }
    this.connectionIsEstablished = false;
    this.handlersRegistered = false;
    this.connectionState$.next(HubConnectionState.Disconnected);
    this.subscribedItems.clear();
    this.subscribedProperties.clear();
    this.subscribedItemPaths.clear();
    this.subscribedPropertyPaths.clear();
  }

  private async reSubscribeAll(): Promise<void> {
    if (!this.hubConnection || this.hubConnection.state !== HubConnectionState.Connected) {
      return;
    }

    // Re-subscribe to all items
    for (const itemId of this.subscribedItems) {
      try {
        const path = this.subscribedItemPaths.get(itemId);
        if (path) {
          await this.hubConnection.invoke('SubscribeToItem', itemId, path);
        }
      } catch (error) {
        console.error(`Error re-subscribing to item ${itemId}:`, error);
      }
    }

    // Re-subscribe to all properties
    for (const [itemId, propertyNames] of this.subscribedProperties.entries()) {
      try {
        const path = this.subscribedPropertyPaths.get(itemId);
        if (path) {
          await this.hubConnection.invoke('SubscribeToProperties', itemId, propertyNames || null, path);
        }
      } catch (error) {
        console.error(`Error re-subscribing to properties of item ${itemId}:`, error);
      }
    }
  }

  private registerEventHandlers(): void {
    // Event 1: Item processing started (all properties)
    this.hubConnection.on('ItemProcessingStarted', (data: ProcessingStatus) => {
      this.updateProcessingStatusFromDTO(data);
    });

    // Event 2: Properties/Tasks processing started
    this.hubConnection.on('PropertiesProcessingStarted', (data: ProcessingStatus) => {
      this.updateProcessingStatusFromDTO(data);
    });

    // Event 3: Task status updated (ratio, message)
    this.hubConnection.on('TaskStatusUpdated', (data: ProcessingStatus) => {
      this.updateProcessingStatusFromDTO(data);
    });

    // Event 4: Item processing done (all properties)
    this.hubConnection.on('ItemProcessingDone', (data: any) => {
      const itemId = typeof data === 'string' ? data : data?.itemId;
      const path = typeof data === 'object' ? data?.path : undefined;

      if (!itemId) {
        return;
      }

      if (!path) {
        // Path is required for all new flows; warn if backend forgets to send it
        console.warn('ItemProcessingDone received without path. This is deprecated and will be ignored.', { itemId, data });
        return;
      }

      const statusKey = this.getStatusKey(String(itemId), String(path));
      const emitter = this.itemDoneEmitters.get(statusKey);
      if (emitter) {
        // Emit the full payload so subscribers can inspect version or any extra metadata
        emitter.emit(data);
      }

      this.removeProcessingStatus(String(itemId), String(path));
    });

    // Event 5: Properties/Tasks processing done
    this.hubConnection.on('PropertiesProcessingDone', (data: any) => {
      const itemId = data?.itemId || (typeof data === 'string' ? data : null);
      const taskNames = data?.taskNames || data?.propertyNames || [];

      if (!itemId) return;

      const current = this.processingStatuses$.value.get(itemId);
      if (current) {
        const updatedTasks = { ...current.processingTasks };
        taskNames.forEach((taskName: string) => {
          delete updatedTasks[taskName];
        });

        if (Object.keys(updatedTasks).length === 0) {
          this.removeProcessingStatus(itemId);
        } else {
          const updatedStatus: ProcessingStatus = {
            ...current,
            processingTasks: updatedTasks,
            lastUpdatedAt: new Date().toISOString()
          };
          this.updateProcessingStatusFromStatus(updatedStatus);
        }
      }
    });
  }

  // Generate composite key for tracking status by itemId + path
  private getStatusKey(itemId: string, path: string): string {
    return `${itemId}::${path || ''}`;
  }

  private updateProcessingStatusFromDTO(data: ProcessingStatus): void {
    if (!data || !data.itemId) return;

    const statusKey = this.getStatusKey(data.itemId, data.path);
    const current = this.processingStatuses$.value.get(statusKey);
    const mergedTasks = current?.processingTasks ? { ...current.processingTasks } : {};

    // Merge new tasks with existing ones
    if (data.processingTasks) {
      Object.assign(mergedTasks, data.processingTasks);
    }

    const updatedStatus: ProcessingStatus = {
      itemId: data.itemId,
      path: data.path || current?.path || '',
      processingTasks: mergedTasks,
      processingProperties: Object.keys(mergedTasks), // Backward compatibility
      status: data.status || 'Processing',
      startedAt: data.startedAt || current?.startedAt || new Date().toISOString(),
      lastUpdatedAt: data.lastUpdatedAt || new Date().toISOString(),
      payload: data.payload
    };

    this.updateProcessingStatusFromStatus(updatedStatus);
  }

  private updateProcessingStatusFromStatus(status: ProcessingStatus): void {
    const current = this.processingStatuses$.value;
    const updated = new Map(current);
    const statusKey = this.getStatusKey(status.itemId, status.path);
    updated.set(statusKey, status);
    this.processingStatuses$.next(updated);
  }

  private updateProcessingStatus(itemId: string, path: string, properties: string[], status: 'Processing' | 'Done'): void {
    // Backward compatibility method
    const current = this.processingStatuses$.value.get(itemId);
    const existingTasks = current?.processingTasks || {};

    // Convert properties to tasks if needed
    const tasks: { [key: string]: ProcessingTaskInfo } = {};
    properties.forEach(prop => {
      if (!existingTasks[prop]) {
        tasks[prop] = {
          taskName: prop,
          startedAt: new Date().toISOString()
        };
      } else {
        tasks[prop] = existingTasks[prop];
      }
    });

    const updatedStatus: ProcessingStatus = {
      itemId,
      path,
      processingTasks: tasks,
      processingProperties: properties, // Backward compatibility
      status,
      startedAt: current?.startedAt || new Date().toISOString(),
      lastUpdatedAt: new Date().toISOString()
    };

    this.updateProcessingStatusFromStatus(updatedStatus);
  }

  private removeProcessingStatus(itemId: string, path?: string): void {
    const current = this.processingStatuses$.value;
    const updated = new Map(current);

    if (path) {
      // Remove specific path
      const statusKey = this.getStatusKey(itemId, path);
      updated.delete(statusKey);
    } else {
      // Remove all paths for this itemId (backward compatibility)
      for (const key of updated.keys()) {
        if (key.startsWith(`${itemId}::`)) {
          updated.delete(key);
        }
      }
    }

    this.processingStatuses$.next(updated);
  }

  /**
   * Subscribe to receive ALL updates for a specific item
   * Supports both async/await and subscribe pattern
   * @param itemId The ID of the item to subscribe to (can be string, number, or GUID)
   * @param path The path of the item (e.g., "/profile", "/contact") to differentiate item types. Required.
   * @returns Promise that resolves when subscription is complete (for async/await)
   */
  async subscribeToItem(itemId: string | number, path: string): Promise<void> {
    // Convert to string to support both GUID and number IDs
    const normalizedItemId = String(itemId || '');
    if (!normalizedItemId) {
      return;
    }

    if (!path) {
      console.warn('subscribeToItem called without path. Path is required to differentiate item types.', { itemId });
    }

    await this.ensureConnection();

    if (!this.hubConnection || this.hubConnection.state !== HubConnectionState.Connected) {
      console.warn('Cannot subscribe: hub connection not available');
      return;
    }

    try {
      // SignalR groups support any characters including hyphens (-)
      // Path is included in group name to differentiate item types (e.g., profile ID=123 vs contact ID=123)
      await this.hubConnection.invoke('SubscribeToItem', normalizedItemId, path);
      this.subscribedItems.add(normalizedItemId);
      this.subscribedItemPaths.set(normalizedItemId, path);
    } catch (error) {
      console.error(`Error subscribing to item ${normalizedItemId}:`, error);
      throw error;
    }
  }

  /**
   * Subscribe to receive updates for specific properties of an item
   * Supports both async/await and subscribe pattern
   *
   * Note: If you only want to listen to specific tasks (e.g., only 'SeoInfo'),
   * use this method with propertyNames. It will NOT subscribe to all item updates,
   * only the specified tasks. This is useful for component children that only
   * need specific task updates.
   *
   * @param itemId The ID of the item (can be string, number, or GUID)
   * @param propertyNames List of property names to subscribe to. If empty or null, subscribe to all (same as subscribeToItem)
   * @param path The path of the item (e.g., "/profile", "/contact") to differentiate item types. Required.
   * @returns Promise that resolves when subscription is complete (for async/await)
   */
  async subscribeToProperties(itemId: string | number, path: string, propertyNames: string[] | undefined): Promise<void> {
    // Convert to string to support both GUID and number IDs
    const normalizedItemId = String(itemId || '');
    if (!normalizedItemId) {
      return;
    }

    if (!path) {
      console.warn('subscribeToProperties called without path. Path is required to differentiate item types.', { itemId, propertyNames });
    }

    await this.ensureConnection();

    if (!this.hubConnection || this.hubConnection.state !== HubConnectionState.Connected) {
      console.warn('Cannot subscribe: hub connection not available');
      return;
    }

    try {
      // SignalR groups support any characters including hyphens (-)
      // Path is included in group name to differentiate item types (e.g., profile ID=123 vs contact ID=123)
      await this.hubConnection.invoke('SubscribeToProperties', normalizedItemId, propertyNames || null, path);
      this.subscribedProperties.set(normalizedItemId, propertyNames || []);
      this.subscribedPropertyPaths.set(normalizedItemId, path);
    } catch (error) {
      console.error(`Error subscribing to properties of item ${normalizedItemId}:`, error);
      throw error;
    }
  }

  /**
   * Ensure the connection is established, waiting if necessary
   * Supports both async/await and handles reconnection
   */
  private async ensureConnection(): Promise<void> {
    // If not initialized, initialize now
    if (!this.hubConnection) {
      await this.initializeConnection();
      return;
    }

    const currentState = this.hubConnection.state;

    if (currentState === HubConnectionState.Connected) {
      return; // Already connected
    }

    if (currentState === HubConnectionState.Connecting) {
      // Wait for connection to complete with timeout
      await this.waitForConnection(10000); // 10 seconds max
      return;
    }

    // If disconnected or disconnecting, try to start
    if (currentState === HubConnectionState.Disconnected ||
        currentState === HubConnectionState.Disconnecting) {
      try {
        await this.hubConnection.start();
        this.connectionState$.next(this.hubConnection.state);
        await this.reSubscribeAll();
      } catch (error) {
        console.error('Error starting connection:', error);
        // Try to reinitialize
        this.hubConnection = null;
        this.handlersRegistered = false;
        await this.initializeConnection();
      }
    }
  }

  /**
   * Wait for connection to be established with timeout
   */
  private async waitForConnection(maxWaitMs: number = 10000): Promise<void> {
    if (!this.hubConnection) {
      return;
    }

    return new Promise<void>((resolve) => {
      const startTime = Date.now();
      const checkInterval = setInterval(() => {
        if (!this.hubConnection) {
          clearInterval(checkInterval);
          resolve();
          return;
        }

        const state = this.hubConnection.state;
        if (state === HubConnectionState.Connected) {
          clearInterval(checkInterval);
          resolve();
        } else if (Date.now() - startTime > maxWaitMs) {
          clearInterval(checkInterval);
          console.warn('Timeout waiting for ProcessingStatusHub connection');
          resolve(); // Resolve anyway to prevent hanging
        }
      }, 100);
    });
  }

  /**
   * Check if a specific property of an item is currently being processed
   * @param itemId The ID of the item (can be string, number, or GUID)
   * @param propertyName The property/task name
   */
  isPropertyProcessing(itemId: string | number, path: string, propertyName: string): boolean {
    return this.isTaskProcessing(itemId, path, propertyName);
  }

  /**
   * Check if a specific task of an item is currently being processed
   * @param itemId The ID of the item (can be string, number, or GUID)
   * @param path The path of the item (e.g., "/profile", "/contact") to differentiate item types. Required.
   * @param taskName The task name
   */
  isTaskProcessing(itemId: string | number, path: string, taskName: string): boolean {
    const normalizedItemId = String(itemId || '');
    const statusKey = this.getStatusKey(normalizedItemId, path);
    const status = this.processingStatuses$.value.get(statusKey);
    return status?.processingTasks?.[taskName] != null || false;
  }

  /**
   * Get task information for a specific task
   * @param itemId The ID of the item (can be string, number, or GUID)
   * @param path The path of the item (e.g., "/profile", "/contact") to differentiate item types. Required.
   * @param taskName The task name
   */
  getTaskInfo(itemId: string | number, path: string, taskName: string): ProcessingTaskInfo | undefined {
    const normalizedItemId = String(itemId || '');
    const statusKey = this.getStatusKey(normalizedItemId, path);
    const status = this.processingStatuses$.value.get(statusKey);
    return status?.processingTasks?.[taskName];
  }

  /**
   * Get all task names for an item
   * @param itemId The ID of the item (can be string, number, or GUID)
   * @param path The path of the item (e.g., "/profile", "/contact") to differentiate item types. Required.
   */
  getTaskNames(itemId: string | number, path: string): string[] {
    const normalizedItemId = String(itemId || '');
    const statusKey = this.getStatusKey(normalizedItemId, path);
    const status = this.processingStatuses$.value.get(statusKey);
    return status?.processingTasks ? Object.keys(status.processingTasks) : [];
  }

  /**
   * Get processing status for a specific item
   * @param itemId The ID of the item (can be string, number, or GUID)
   * @param path The path of the item (e.g., "/profile", "/contact") to differentiate item types. Required.
   */
  getProcessingStatus(itemId: string | number, path: string): ProcessingStatus | undefined {
    const normalizedItemId = String(itemId || '');
    const statusKey = this.getStatusKey(normalizedItemId, path);
    return this.processingStatuses$.value.get(statusKey);
  }

  /**
   * Observable that emits when a specific item's DONE signal is received
   * (ItemProcessingDone from the hub). Caller should register themself
   * subscribeToItem(itemId, path) to receive the processing updates, then use this observable to listen for the DONE event.
   */
  onItemDone(itemId: string | number, path: string): Observable<any> {
    const statusKey = this.getStatusKey(String(itemId || ''), String(path || ''));
    let emitter = this.itemDoneEmitters.get(statusKey);
    if (!emitter) {
      emitter = new EventEmitter<any>();
      this.itemDoneEmitters.set(statusKey, emitter);
    }
    return emitter.asObservable();
  }

  /**
   * Observable for processing statuses
   */
  getProcessingStatuses$(): Observable<Map<string, ProcessingStatus>> {
    return this.processingStatuses$.asObservable();
  }

  /**
   * Observable for connection state
   */
  getConnectionState$(): Observable<HubConnectionState> {
    return this.connectionState$.asObservable();
  }

  /**
   * Cleanup on service destroy
   */
  async disconnect(): Promise<void> {
    if (this.hubConnection && this.hubConnection.state !== HubConnectionState.Disconnected) {
      await this.hubConnection.stop();
    }
  }
}
